import { dehydrate } from '@tanstack/react-query';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';

import { getSeoTags } from '@api/seo/api';

import MobileAccordionItem from '@components/MobileAccordionItem';
import PageSection from '@components/PageSection';
import PageWrapper from '@components/PageWrapper';

import { getCommonProps } from '@scripts/server/getCommonProps';
import { ExtendedSSRDataProps } from '@scripts/server/types';

import PhoneIcon from '@icons/18/phone.svg';
import MailIcon from '@icons/mail.svg';

import RegionalOfficesSection from './components/RegionalOfficesSection/RegionalOfficesSection';
import RequisitesSection from './components/RequisitesSection/RequisitesSection';
import TopContactsCards from './components/TopContactsCards/TopContactsCards';
import { CITY_OFFICES, CONTACT_AUDIENCE_CARDS, CONTACT_PAGE_TITLE, REQUISITES_SECTION_PROPS } from './constants';
import styles from './ContactsView.module.scss';
import { ContactsPageProps } from './types';

const ContactsPage = ({ seoData }: ContactsPageProps) => {
    const { title = '', description = '' } = seoData?.data || {};

    return (
        <PageWrapper
            seoProps={{
                title: title || CONTACT_PAGE_TITLE,
                description,
                nameDigi: 'contacts',
                contentDigi: 'Digi Contacts Page',
            }}
        >
            <PageSection
                className={styles.contactsView_section}
                containerClassName={styles.contactsView_container}
                fullHeight
            >
                <h1 className={styles.contactsView_pageTitle}>{CONTACT_PAGE_TITLE}</h1>
                <div className={styles.contactsView_desktopSections}>
                    <TopContactsCards cards={CONTACT_AUDIENCE_CARDS} />

                    <div className={styles.contactsView_divider} role="presentation" />

                    <RegionalOfficesSection offices={CITY_OFFICES} />

                    <div className={styles.contactsView_divider} role="presentation" />

                    <RequisitesSection {...REQUISITES_SECTION_PROPS} />
                </div>
                <div className={styles.contactsView_mobileSections}>
                    {CONTACT_AUDIENCE_CARDS.map((card, index) => (
                        <MobileAccordionItem key={card.id} title={card.title} defaultOpen={index === 0}>
                            <p className={styles.contactsView_mobileText}>{card.schedule}</p>
                            <div className={styles.contactsView_mobileContacts}>
                                <div className={styles.contactsView_mobileContactRow}>
                                    <span className={styles.contactsView_mobileContactIcon} aria-hidden>
                                        <PhoneIcon />
                                    </span>
                                    <a href={`tel:${card.phoneTel}`} className={styles.contactsView_mobileLink}>
                                        {card.phoneLabel}
                                    </a>
                                </div>
                                <div className={styles.contactsView_mobileContactRow}>
                                    <span className={styles.contactsView_mobileContactIcon} aria-hidden>
                                        <MailIcon />
                                    </span>
                                    <a href={`mailto:${card.email}`} className={styles.contactsView_mobileLink}>
                                        {card.email}
                                    </a>
                                </div>
                            </div>
                        </MobileAccordionItem>
                    ))}

                    <MobileAccordionItem title="Региональные офисы и склады">
                        <RegionalOfficesSection offices={CITY_OFFICES} hideTitle />
                    </MobileAccordionItem>

                    <MobileAccordionItem title="Реквизиты">
                        <RequisitesSection {...REQUISITES_SECTION_PROPS} hideTitle />
                    </MobileAccordionItem>
                </div>
            </PageSection>
        </PageWrapper>
    );
};

export default ContactsPage;

export const getServerSideProps = async (
    context: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<ExtendedSSRDataProps>> =>
    getCommonProps(context, async (props, queryClient, apiClient) => {
        let seoData: ContactsPageProps['seoData'] | null = null;

        try {
            const { key: seoTagsKey, fetch: baseSeoTagsFetch } = getSeoTags(apiClient)({
                filter: {
                    url: '/contacts',
                    type: 'other',
                },
            });

            seoData = await queryClient.fetchQuery({
                queryKey: seoTagsKey,
                queryFn: baseSeoTagsFetch,
            });
        } catch {
            seoData = null;
        }

        return {
            props: {
                ...props,
                dehydratedState: dehydrate(queryClient),
                ...(seoData ? { seoData } : {}),
            },
        };
    });
